import { Input, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../redux/reducers';
import { ServiceCategory, NewServiceCategory } from '../../../models/Entities/ServiceCategory';

// i18Next
import { useTranslation } from 'react-i18next';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// store
import * as ServiceCategoryStore from '../../../redux/middleware/ServiceCategoryMiddleware';

interface FormValidation {
  success: boolean;
  errors: string[];
}

export interface IProps {
  isOpen: boolean;
  dismissPanel: () => void;
  ServiceCategory?: ServiceCategory;
}

export const ServiceCategoryFormComponent = (props: IProps) => {
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [FormValidationStatus, setFormValidationStatus] = useState<
    FormValidation | undefined
  >();

  const dispatch = useDispatch();
  const [t] = useTranslation();

  // state
  const ServiceCategoryState = useSelector((appState: AppState) => appState.ServiceCategories);

  // entity properties
  const [description, setDescription] = useState<string>('');

  // effects
  useEffect(() => {
    if (props.ServiceCategory) {
      setDescription(props.ServiceCategory.description || '');
    } else {
      ResetForm();
    }
  }, [props.ServiceCategory]);

  useEffect(() => {
    if (
      submittedForm &&
      ServiceCategoryState &&
      (ServiceCategoryState.isAddingSuccess || ServiceCategoryState.isSavingSuccess)
    ) {
      // Small delay to ensure state is updated before closing
      setTimeout(() => {
        ResetForm();
        props.dismissPanel();
      }, 100);
    }
  }, [
    submittedForm,
    ServiceCategoryState?.isAddingSuccess,
    ServiceCategoryState?.isSavingSuccess,
    props.dismissPanel
  ]);

  const ResetForm = () => {
    setDescription('');
    setSubmittedForm(false);
    setFormValidationStatus(undefined);
  };

  // Events
  const onChangeDescription = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(ev.target.value);
  };

  const HandleSaveForm = () => {
    setSubmittedForm(true);
    if (ValidateForm()) {
      if (props.ServiceCategory) {
        // Update existing category
        const updatedCategory: ServiceCategory = {
          id: props.ServiceCategory.id,
          description: description,
          created: props.ServiceCategory.created,
          createdBy: props.ServiceCategory.createdBy
        };
        dispatch(ServiceCategoryStore.actionCreators.UpdateServiceCategory(updatedCategory));
      } else {
        // Add new category
        const newCategory: NewServiceCategory = {
          description: description
        };
        dispatch(ServiceCategoryStore.actionCreators.AddServiceCategory(newCategory));
      }
    }
  };

  const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
  };

  // form validation
  const ValidateForm = (): boolean => {
    let validationResult: boolean = true;
    let FormValidation: FormValidation = { success: true, errors: [] };
    setFormValidationStatus(FormValidation);

    if (!description || description.trim() === '') {
      validationResult = false;
      FormValidation.success = false;
      FormValidation.errors.push(t('serviceCategoryForm.validation.requiredDescription'));
    }

    if (description && description.length > 200) {
      validationResult = false;
      FormValidation.success = false;
      FormValidation.errors.push(t('serviceCategoryForm.validation.descriptionTooLong'));
    }

    FormValidation.success = validationResult;
    setFormValidationStatus(FormValidation);
    return validationResult;
  };

  const isSaving = !!(ServiceCategoryState?.isAdding || ServiceCategoryState?.isSaving);
  const saveError =
    ServiceCategoryState &&
    (ServiceCategoryState.failOnAddingNewOne || ServiceCategoryState.failOnUpdatingOne)
      ? ServiceCategoryState.error
      : undefined;

  return (
    <GuideDrawer
      open={props.isOpen}
      size="small"
      title={props.ServiceCategory ? t('serviceCategoryForm.titleEdit') : t('serviceCategoryForm.title')}
      onDismiss={props.dismissPanel}
      testId="service-category-form-drawer"
      secondaryAction={{ label: t('serviceCategoryForm.cancelButton'), onClick: HandleCancelForm }}
      primaryAction={{ label: t('serviceCategoryForm.saveButton'), onClick: HandleSaveForm, loading: isSaving }}
    >
      {submittedForm && !FormValidationStatus?.success && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{t('serviceCategoryForm.reviewInformation')}</MessageBarTitle>
            <ul>
              {FormValidationStatus?.errors.map(
                (Error: string, index: number) => (
                  <li key={index}>{Error}</li>
                )
              )}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {saveError && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            {saveError.ErrorMessage}
            {saveError.Errors && saveError.Errors.length > 0 && (
              <ul>{saveError.Errors.map((item: string, index: number) => <li key={index}>{item}</li>)}</ul>
            )}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideDrawerForm onSubmit={() => !isSaving && HandleSaveForm()} testId="service-category-form">
        <GuideField
          label={t('serviceCategoryForm.fields.description')}
          required
          error={
            submittedForm === true && (!description || description.trim() === '')
              ? t('serviceCategoryForm.validation.requiredDescription')
              : undefined
          }
        >
          <Input
            name="txt_description"
            onChange={onChangeDescription}
            value={description}
            placeholder={t('serviceCategoryForm.fields.descriptionPlaceholder')}
          />
        </GuideField>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default ServiceCategoryFormComponent;
